import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Lock, Sparkles, X } from "lucide-react";
import { api } from "../lib/api";

/**
 * PackPreviewSheet — bottom sheet shown when a user taps a locked premium
 * pack. Prefers a curated `pack.preview` list (top-3 prompts) from the
 * backend, falling back to a start/middle/70% spread across the full pack.
 *
 * Props:
 *   pack       — display meta { id, name, accent, img, icon|emoji, description }
 *   onClose()  — dismiss the sheet
 *   onUnlock() — user tapped Unlock → opens paywall in caller
 */
export default function PackPreviewSheet({ pack, onClose, onUnlock }) {
  const [previewItems, setPreviewItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!pack?.id) return;
    setLoading(true);
    setPreviewItems([]);
    api.getPack(pack.id)
      .then((data) => {
        // 1st choice — curated "hero" preview from backend
        const curated = Array.isArray(data?.preview) ? data.preview.filter(Boolean) : [];
        if (curated.length >= 3) {
          setPreviewItems(curated.slice(0, 3));
          return;
        }
        // Fallback — pull start / middle / 70% slot from the full prompt list
        const all = data?.items?.truth_or_dare || data?.items?.group_truth_or_dare || [];
        const indices = [
          0,
          Math.floor(all.length / 2),
          Math.min(all.length - 1, Math.floor(all.length * 0.72)),
        ].filter((v, i, arr) => arr.indexOf(v) === i);
        const picks = indices
          .map((i) => all[i])
          .filter(Boolean)
          .map((p) => p.truth || p.prompt || "")
          .filter(Boolean);
        // Top up with curated if we still have less than 3
        const mixed = [...curated, ...picks].filter((v, i, arr) => arr.indexOf(v) === i);
        setPreviewItems(mixed.slice(0, 3));
      })
      .catch(() => setPreviewItems([]))
      .finally(() => setLoading(false));
  }, [pack?.id]);

  const accent = pack?.accent || "#FF2E9A";

  return (
    <AnimatePresence>
      {pack && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center"
          style={{ background: "rgba(4, 2, 8, 0.72)", backdropFilter: "blur(8px)" }}
          onClick={onClose}
          data-testid="pack-preview-sheet"
        >
          <motion.div
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 26 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full sm:max-w-[420px] rounded-t-3xl sm:rounded-3xl overflow-hidden relative"
            style={{
              background:
                "linear-gradient(180deg, rgba(20, 10, 30, 0.95) 0%, rgba(8, 4, 14, 0.98) 100%)",
              border: `1.5px solid ${accent}80`,
              boxShadow: `0 0 36px ${accent}66, inset 0 0 24px ${accent}18`,
            }}
          >
            <button
              onClick={onClose}
              data-testid="pack-preview-close"
              className="absolute top-3.5 right-3.5 w-9 h-9 rounded-full flex items-center justify-center text-white/70 hover:text-white transition"
              style={{
                background: "rgba(255, 255, 255, 0.08)",
                border: "1px solid rgba(255, 255, 255, 0.12)",
              }}
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="px-5 pt-7 pb-5">
              {/* Header */}
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
                  style={{
                    background: `radial-gradient(circle, ${accent}40 0%, rgba(8, 4, 12, 0.85) 75%)`,
                    border: `1px solid ${accent}80`,
                    boxShadow: `0 0 14px ${accent}80`,
                  }}
                >
                  {pack.img ? (
                    <img
                      src={pack.img}
                      alt=""
                      className="w-12 h-12 object-contain"
                      style={{ filter: `drop-shadow(0 0 8px ${accent})` }}
                    />
                  ) : (
                    <span className="text-3xl">{pack.icon || pack.emoji}</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span
                      className="inline-flex items-center text-[9px] font-black tracking-[0.18em] px-1.5 py-[2px] rounded-full uppercase"
                      style={{
                        background: "linear-gradient(135deg, #FF2E9A, #FF6B47)",
                        color: "#FFFFFF",
                        border: "1px solid rgba(255, 255, 255, 0.32)",
                        boxShadow: "0 0 6px rgba(255, 46, 154, 0.55)",
                      }}
                    >
                      <Lock className="w-2.5 h-2.5 mr-0.5" /> Premium
                    </span>
                  </div>
                  <h3 className="text-white text-[19px] font-extrabold tracking-tight leading-tight">
                    {pack.name}
                  </h3>
                  <p className="text-white/65 text-[12px] mt-0.5 leading-snug">
                    {pack.description}
                  </p>
                </div>
              </div>

              {/* Peek headline */}
              <div className="flex items-center gap-2 mb-2.5">
                <Sparkles className="w-4 h-4" style={{ color: accent }} />
                <span className="text-white/85 text-[12px] font-extrabold tracking-[0.16em] uppercase">
                  A taste of what&rsquo;s inside
                </span>
              </div>

              {/* Preview prompts */}
              <div className="space-y-2 mb-4" data-testid="pack-preview-prompts">
                {loading ? (
                  <div className="text-white/55 text-[13px] py-6 flex items-center justify-center">
                    <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading peek…
                  </div>
                ) : previewItems.length ? (
                  previewItems.map((p, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.06 * i, duration: 0.28 }}
                      className="rounded-xl px-3 py-2.5 flex items-start gap-2.5"
                      style={{
                        background: `${accent}10`,
                        border: `1px solid ${accent}45`,
                      }}
                    >
                      <span
                        className="text-[14px] font-extrabold"
                        style={{ color: accent }}
                      >
                        “
                      </span>
                      <span className="text-white text-[13.5px] font-semibold leading-snug flex-1">
                        {p}
                      </span>
                    </motion.div>
                  ))
                ) : (
                  <div className="text-white/55 text-[13px] py-4 text-center">
                    More good stuff inside — unlock to see.
                  </div>
                )}
              </div>

              {/* CTA */}
              <button
                onClick={onUnlock}
                data-testid="pack-preview-unlock"
                className="vc-cta vc-cta-primary w-full rounded-full py-[14px] font-extrabold inline-flex items-center justify-center gap-2"
                style={{ fontSize: "15.5px" }}
              >
                <Lock className="w-4 h-4" strokeWidth={3} />
                Unlock for $4.99 lifetime
              </button>
              <button
                onClick={onClose}
                className="vc-cta vc-cta-link w-full mt-2 py-2 text-white/55 text-[12.5px] font-semibold hover:text-white transition rounded-full"
              >
                Maybe later
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
